import { selectQuotationRoofBoxStructure } from "app/containers/WorkOrder/redux/selector";
import { actions } from "app/containers/WorkOrder/redux/slice";
import {
  AddOnItemFormValue,
  QuotationRoofBoxCardKey,
} from "app/containers/WorkOrder/types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CURTAIN_FRONT_WALL_OPTIONS,
  CURTAIN_REAR_FRAME_OPTIONS,
  CURTAIN_REAR_OPENING_OPTIONS,
  CURTAIN_ROOF_BEAM_OPTIONS,
  CURTAIN_ROOF_TYPE_OPTIONS,
  CURTAIN_SIDE_DOOR_OPTIONS,
} from "../../../data";
import {
  boxKey,
  formKey,
  HEIGHT_KEY,
  NOTE,
  TYPE_KEY,
} from "../../../hooks/useQuoteBoxStructure";
import { QuoteSelectOption } from "../../../types";
import {
  FieldsRow,
  StructureCard,
} from "../OpenBoxQuotes/BoxStructureFields";
import { NoteField, NumberField, SelectField } from "../OpenBoxQuotes/FormFields";

export const roofKey = (suffix: string) => formKey(`ROOF_CURTAINS.${suffix}`);

export const ROOF_TYPE = CURTAIN_ROOF_TYPE_OPTIONS;
export const ROOF_BEAM = CURTAIN_ROOF_BEAM_OPTIONS;
export const FRONT_WALL = CURTAIN_FRONT_WALL_OPTIONS;
export const REAR_FRAME = CURTAIN_REAR_FRAME_OPTIONS;
export const REAR_OPENING = CURTAIN_REAR_OPENING_OPTIONS;
export const SIDE_DOOR = CURTAIN_SIDE_DOOR_OPTIONS;

const CARD_TITLES: Record<QuotationRoofBoxCardKey, string> = {
  roofType: "ROOF_TYPE",
  hydraulicRoof: "HYDRAULIC_ROOF",
  detachableRoof: "DETACHABLE_ROOF",
  longitudinalBeams: "LONGITUDINAL_BEAMS",
  middlePillar: "MIDDLE_PILLAR",
  frontWall: "FRONT_WALL",
  rearFrame: "REAR_FRAME",
  rearOpening: "REAR_OPENING",
  hydraulicSide: "HYDRAULIC_SIDE",
  sideCompletionFlap: "SIDE_COMPLETION_FLAP",
  sideDoors: "SIDE_DOORS",
  sidePillars: "SIDE_PILLARS",
  plywoodInsteadOfCurtain: "PLYWOOD_INSTEAD_OF_CURTAIN",
  curtainTypeAndTensioners: "CURTAIN_TYPE_AND_TENSIONERS",
  tensionType: "TENSION_TYPE",
  curtainRail: "CURTAIN_RAIL",
  curtainEdge: "CURTAIN_EDGE",
};

export const useRoofBoxStructure = () => {
  const dispatch = useDispatch();
  const data = useSelector(selectQuotationRoofBoxStructure);
  const patch = (
    key: QuotationRoofBoxCardKey,
    value: Partial<AddOnItemFormValue>
  ) => dispatch(actions.updateQuotationRoofBoxStructure({ key, value }));

  return { data, patch };
};

const titleKey = (cardKey: QuotationRoofBoxCardKey) =>
  roofKey(`CARDS.${CARD_TITLES[cardKey]}`);

export const TypeCard = ({
  cardKey,
  options,
}: {
  cardKey: QuotationRoofBoxCardKey;
  options: QuoteSelectOption[];
}) => {
  const { data, patch } = useRoofBoxStructure();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey(cardKey)}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <SelectField
        labelKey={TYPE_KEY}
        value={item.selectedOption}
        options={options}
        onChange={(selectedOption) => patch(cardKey, { selectedOption })}
      />
    </StructureCard>
  );
};

export const NoteCard = ({
  cardKey,
}: {
  cardKey: QuotationRoofBoxCardKey;
}) => {
  const { data, patch } = useRoofBoxStructure();
  const item = data[cardKey];

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey(cardKey)}
      onToggle={(enabled) => patch(cardKey, { enabled })}
    >
      <NoteField
        labelKey={NOTE}
        placeholderKey={NOTE}
        value={item.note}
        onChange={(note) => patch(cardKey, { note })}
      />
    </StructureCard>
  );
};

export const SideDoorsCard = () => {
  const { data, patch } = useRoofBoxStructure();
  const item = data.sideDoors;

  return (
    <StructureCard
      enabled={item.enabled}
      titleKey={titleKey("sideDoors")}
      onToggle={(enabled) => patch("sideDoors", { enabled })}
    >
      <FieldsRow>
        <SelectField
          labelKey={TYPE_KEY}
          value={item.selectedOption}
          options={SIDE_DOOR}
          onChange={(selectedOption) =>
            patch("sideDoors", { selectedOption })
          }
          grow
        />
        <NumberField
          labelKey={HEIGHT_KEY}
          value={item.size}
          onChange={(size) => patch("sideDoors", { size })}
          grow
        />
        <NumberField
          labelKey={boxKey("FIELDS.QUANTITY")}
          value={item.quantity}
          onChange={(quantity) => patch("sideDoors", { quantity })}
          grow
        />
      </FieldsRow>
    </StructureCard>
  );
};
